import { useState, useCallback } from "react";
import { usePersistedState } from "./usePersistedState";

/**
 * useToolbarState Hook
 *
 * Unified state management for all toolbar controls with automatic persistence.
 * Handles view mode, search, sort, filters, and pagination with configurable
 * persistence levels (user/session/none).
 *
 * @param {string} toolId - Unique tool identifier (e.g., 'bannerAds', 'properties', 'jobs')
 * @param {Object} config - Configuration for each control
 * @param {Object} config.viewMode - View mode configuration
 * @param {string} config.viewMode.default - Default view mode ('cards' | 'list')
 * @param {string} config.viewMode.persist - Persistence level ('user' | 'session' | 'none')
 * @param {Object} config.search - Search configuration
 * @param {string} config.search.default - Default search value
 * @param {string} config.search.persist - Persistence level
 * @param {Object} config.sort - Sort configuration
 * @param {string} config.sort.default - Default sort value
 * @param {string} config.sort.persist - Persistence level
 * @param {Object} config.filter - Filter configuration
 * @param {string} config.filter.default - Default filter value
 * @param {string} config.filter.persist - Persistence level
 * @param {Object} config.page - Page configuration
 * @param {number} config.page.default - Default page number
 * @param {string} config.page.persist - Persistence level
 * @param {Object} config.perPage - Per-page configuration
 * @param {number} config.perPage.default - Default items per page
 * @param {string} config.perPage.persist - Persistence level
 *
 * @returns {Object} State and setters for all controls
 *
 * @example
 * // Basic usage with defaults
 * const toolbar = useToolbarState('bannerAds', {
 *   viewMode: { default: 'cards', persist: 'user' },
 *   search: { persist: 'session' },
 *   sort: { default: 'views', persist: 'session' },
 * });
 *
 * const { viewMode, search, sort } = toolbar;
 * const { setViewMode, setSearch, setSort } = toolbar;
 *
 * @example
 * // Minimal configuration (only what you need)
 * const toolbar = useToolbarState('redirects', {
 *   viewMode: { default: 'list' },
 * });
 */
export function useToolbarState(toolId, config = {}) {
  // Extract configuration with defaults
  const {
    viewMode: {
      default: defaultViewMode = "cards",
      persist: persistViewMode = "user", // 'user' | 'session' | 'none'
    } = {},

    search: {
      default: defaultSearch = "",
      persist: persistSearch = "session",
    } = {},

    sort: {
      default: defaultSort = null,
      persist: persistSort = "session",
    } = {},

    filter: {
      default: defaultFilter = "all",
      persist: persistFilter = "session",
    } = {},

    page: {
      default: defaultPage = 1,
      persist: persistPage = "none", // Usually don't persist page
    } = {},

    perPage: {
      default: defaultPerPage = 25,
      persist: persistPerPage = "session",
    } = {},
  } = config;

  // Create state for each control
  // Always call usePersistedState (it handles 'none' internally)

  // ViewMode
  const [viewMode, setViewMode] = usePersistedState(
    `user.tools.${toolId}.viewMode`,
    defaultViewMode,
    { storage: persistViewMode === "none" ? "none" : persistViewMode === "user" ? "local" : "session" }
  );

  // Search
  const [search, setSearch] = usePersistedState(
    `session.${toolId}.search`,
    defaultSearch,
    { storage: persistSearch === "none" ? "none" : persistSearch === "user" ? "local" : "session" }
  );

  // Sort
  const [sort, setSort] = usePersistedState(
    `session.${toolId}.sort`,
    defaultSort,
    { storage: persistSort === "none" ? "none" : persistSort === "user" ? "local" : "session" }
  );

  // Filter
  const [filter, setFilter] = usePersistedState(
    `session.${toolId}.filter`,
    defaultFilter,
    { storage: persistFilter === "none" ? "none" : persistFilter === "user" ? "local" : "session" }
  );

  // Page
  const [page, setPage] = usePersistedState(
    `session.${toolId}.page`,
    defaultPage,
    { storage: persistPage === "none" ? "none" : persistPage === "user" ? "local" : "session" }
  );

  // PerPage
  const [perPage, setPerPage] = usePersistedState(
    `session.${toolId}.perPage`,
    defaultPerPage,
    { storage: persistPerPage === "none" ? "none" : persistPerPage === "user" ? "local" : "session" }
  );

  // Helper to reset all session-level state
  const resetFilters = useCallback(() => {
    if (persistSearch !== "none") setSearch(defaultSearch);
    if (persistSort !== "none") setSort(defaultSort);
    if (persistFilter !== "none") setFilter(defaultFilter);
    if (persistPage !== "none") setPage(defaultPage);
  }, [
    defaultSearch,
    defaultSort,
    defaultFilter,
    defaultPage,
    persistSearch,
    persistSort,
    persistFilter,
    persistPage,
    setSearch,
    setSort,
    setFilter,
    setPage,
  ]);

  // Helper to reset everything including user preferences
  const resetAll = useCallback(() => {
    setViewMode(defaultViewMode);
    setSearch(defaultSearch);
    setSort(defaultSort);
    setFilter(defaultFilter);
    setPage(defaultPage);
    setPerPage(defaultPerPage);
  }, [
    defaultViewMode,
    defaultSearch,
    defaultSort,
    defaultFilter,
    defaultPage,
    defaultPerPage,
    setViewMode,
    setSearch,
    setSort,
    setFilter,
    setPage,
    setPerPage,
  ]);

  return {
    // View mode
    viewMode,
    setViewMode,

    // Search
    search,
    setSearch,

    // Sort
    sort,
    setSort,

    // Filter
    filter,
    setFilter,

    // Pagination
    page,
    setPage,
    perPage,
    setPerPage,

    // Utilities
    resetFilters, // Reset session-level state
    resetAll, // Reset everything
  };
}

export default useToolbarState;
